/**
 * Marks: one spot verb across rungs (IDEAS X2). A commander, human or bot, marks a point or a unit for its team.
 * A mark is team knowledge: it lives as a task-tier order of kind `mark` on the team's squads (so it enters the
 * Match as an input like every order, and is resolved to whoever is in the squads when it applies), the team
 * picture reads it as a belief with source `mark` (the tightest fix there is), the bots treat it as a picture
 * target on both clocks, and the topo map and the HUD draw it. A mark fades like a track. At L2 a mark on the
 * sector map crosses down into the battle as a mark or a fire mission (sector/types `marks`). Pure.
 */
import type { GameState, MatchInputBody, MatchSquad, PlayerState, SquadOrder, Vec3 } from '../types';
import { vecNormalize } from '../math';
import { personSetOf } from './scaleProfile';
import { personPointKept, type GroundPointKept } from './fineTerrain';
import { PHYS_DT } from '../constants';
import { confidenceAfterSeconds } from './awareness';
import { dpow } from './dmath';

export type MarkOrder = Extract<SquadOrder, { kind: 'mark' }>;

export interface MarkTask { order: MarkOrder; issuedTick: number; squadId: number }

/** Every mark a team holds now (newest last). */
export function markTasksFor(state: Pick<GameState, 'squads'>, team: number): MarkTask[] {
  // 62.65 (d): kept per squads list and team (the list is copy-on-write: a task added or lapsed is a new list); the brains
  // ask for it up to sixteen times a bot a tick. Shared: read, never written into.
  const squads = state.squads;
  if (!squads) return [];
  let byTeam = tasksKept.get(squads);
  if (!byTeam) tasksKept.set(squads, byTeam = new Map());
  const hit = byTeam.get(team);
  if (hit) return hit;
  const out: MarkTask[] = [];
  for (const sq of squads) {
    if (sq.team !== team) continue;
    for (const t of sq.tasks) if (t.order.kind === 'mark') out.push({ order: t.order, issuedTick: t.issuedTick, squadId: sq.id });
  }
  out.sort((a, b) => a.issuedTick - b.issuedTick);
  byTeam.set(team, out);
  return out;
}
const tasksKept = new WeakMap<readonly MatchSquad[], Map<number, MarkTask[]>>();

/** A mark's position in the world: on its planet's surface along its direction. */
export function markPos(state: Pick<GameState, 'planets' | 'config'>, m: MarkOrder): Vec3 | null {
  const planet = state.planets[m.planetIndex];
  if (!planet) return null;
  // where a person marked there would be: the body's centre over the ground as a person stands on it (the fine ground
  // where the scale has one) — the place the team's other tracks use. It was the planet's nominal sphere, which floats
  // above low ground and sinks under high ground: bots aimed at it, and the owner saw the marks "in the sky"
  const ps = personSetOf(state);
  // 62.65 (d): placed once per order while the ground and the set stand (fineTerrain personPointKept); it was placed on
  // every read — 17 % of the tick at 50 a side with marks on
  return personPointKept(posKept, m, planet, vecNormalize(m.surfaceDir), ps, ps.hitboxCenter);
}
const posKept = new WeakMap<MarkOrder, GroundPointKept>();

/** How sure a mark still is on the action clock, by its age. */
export function markConfidence(issuedTick: number, tick: number): number {
  return confidenceAfterSeconds('mark', Math.max(0, tick - issuedTick) * PHYS_DT);
}

/** How sure a mark still is on the turn clock, by the turns since it was made. */
export function markConfidenceTurns(markTurn: number, turn: number): number {
  const age = Math.max(0, turn - markTurn);
  return Math.max(0, 0.95 * dpow(0.85, age));
}

/**
 * The order bodies a commander sends to mark something for its whole team: one task per squad of the team (a mark is
 * team knowledge, and the squads are where tasks live). `turn` stamps the turn clock's age.
 */
export function markBodies(state: Pick<GameState, 'squads' | 'turnNumber'>, team: number, mark: Omit<MarkOrder, 'kind' | 'turn'>): Array<Extract<MatchInputBody, { kind: 'order' }>> {
  return (state.squads ?? []).filter(sq => sq.team === team).map(sq => ({
    kind: 'order', team, to: { squad: sq.id }, tier: 'task',
    order: { kind: 'mark', ...mark, turn: state.turnNumber },
  }));
}

/** The nearest enemy a unit can see, as a unit mark, or null. */
export function markNearestSeen(state: Pick<GameState, 'players'>, unit: PlayerState, visibleEnemyIds: readonly number[]): Omit<MarkOrder, 'kind' | 'turn'> | null {
  const seen = state.players.filter(p => visibleEnemyIds.includes(p.id) && p.alive && p.teamIndex !== unit.teamIndex);
  if (!seen.length) return null;
  const target = seen.reduce((a, p) => (p.id < a.id ? p : a));
  return { planetIndex: target.planetIndex, surfaceDir: target.surfaceDir, unitId: target.id };
}

/**
 * The turn bot's mark (lane B's check, 62.43): on the turn clock a unit's sight is already its team's, so a mark's worth
 * is making a sighting last — the nearest enemy in sight the team has not marked yet (none when every one is marked).
 */
export function wantsMark(state: Pick<GameState, 'players' | 'squads'>, unit: PlayerState, visibleEnemyIds: readonly number[]): Omit<MarkOrder, 'kind' | 'turn'> | null {
  if (!state.squads?.some(sq => sq.team === unit.teamIndex)) return null;
  const marked = new Set(markTasksFor(state, unit.teamIndex).map(m => m.order.unitId).filter((x): x is number => x !== undefined));
  const fresh = state.players.filter(p => visibleEnemyIds.includes(p.id) && p.alive && p.teamIndex !== unit.teamIndex && !marked.has(p.id));
  if (!fresh.length) return null;
  const near = fresh.filter(p => p.planetIndex === unit.planetIndex);
  const pick = (near.length ? near : fresh).reduce((a, p) => (p.id < a.id ? p : a));
  return { planetIndex: pick.planetIndex, surfaceDir: pick.surfaceDir, unitId: pick.id };
}

/**
 * Drop marks that have faded (the state stays small) — and, 69.85 (a) (DECISIONS 318), a mark of a unit that has died, as
 * an engage of a dead target lapses (the kill is known: core/orderForms.ts teamKnowsUnit). A mark is a fixed place, so it
 * outlived its unit and drew grenades at an empty spot for a minute and more; the unit's next life is a new sighting.
 * The Match runs it every tick on the action clock, so the mark is gone before the unit respawns.
 */
export function pruneMarks(state: GameState, tick: number): GameState {
  if (!state.squads?.some(sq => sq.tasks.some(t => t.order.kind === 'mark'))) return state;
  let changed = false;
  const dead = (id: number | undefined) => id !== undefined && state.players.find(p => p.id === id)?.alive === false;
  const squads: MatchSquad[] = state.squads.map(sq => {
    const tasks = sq.tasks.filter(t => t.order.kind !== 'mark' || (markConfidence(t.issuedTick, tick) > 0.02 && !dead(t.order.unitId)));
    if (tasks.length !== sq.tasks.length) changed = true;
    return tasks.length === sq.tasks.length ? sq : { ...sq, tasks };
  });
  return changed ? { ...state, squads } : state;
}
